import React from 'react';
import { Box, Paper, Typography, useMediaQuery, useTheme, alpha } from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PeopleIcon from '@mui/icons-material/People';

interface SummaryCardData {
  icon: React.ReactNode;
  label: string;
  value: number;
  color: string;
  bgColor: string;
}

interface DashboardSummaryCardsProps {
  totalStudents: number;
  studentsWaiting: number;
  studentsReady: number;
  authorizedPersonsCount: number;
}

const DashboardSummaryCards: React.FC<DashboardSummaryCardsProps> = ({
  totalStudents,
  studentsWaiting,
  studentsReady,
  authorizedPersonsCount,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const summaryData: SummaryCardData[] = [
    {
      icon: <SchoolIcon sx={{ fontSize: { xs: '2rem', sm: '2.5rem' } }} />,
      label: 'Total Students',
      value: totalStudents,
      color: theme.palette.primary.main,
      bgColor: alpha(theme.palette.primary.main, 0.1),
    },
    {
      icon: <HourglassEmptyIcon sx={{ fontSize: { xs: '2rem', sm: '2.5rem' } }} />,
      label: 'Waiting for Pickup',
      value: studentsWaiting,
      color: theme.palette.warning.main,
      bgColor: alpha(theme.palette.warning.main, 0.1),
    },
    {
      icon: <CheckCircleIcon sx={{ fontSize: { xs: '2rem', sm: '2.5rem' } }} />,
      label: 'Ready for Pickup',
      value: studentsReady,
      color: theme.palette.success.main,
      bgColor: alpha(theme.palette.success.main, 0.1),
    },
    {
      icon: <PeopleIcon sx={{ fontSize: { xs: '2rem', sm: '2.5rem' } }} />,
      label: 'Authorized Persons',
      value: authorizedPersonsCount,
      color: theme.palette.info.main,
      bgColor: alpha(theme.palette.info.main, 0.1),
    },
  ];

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, 1fr)',
          lg: 'repeat(4, 1fr)',
        },
        gap: { xs: 2, sm: 2, md: 3 },
        mb: 3,
      }}
    >
      {summaryData.map((card, index) => (
        <Paper
          key={index}
          variant="info-card"
          elevation={2}
          sx={{
            p: { xs: 2, sm: 3 },
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 2,
            bgcolor: card.bgColor,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: { xs: 56, sm: 64 },
              height: { xs: 56, sm: 64 },
              borderRadius: 2,
              bgcolor: 'background.paper',
              color: card.color,
              flexShrink: 0,
            }}
          >
            {card.icon}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="h3"
              sx={{
                fontSize: { xs: '1.75rem', sm: '2.125rem' },
                color: card.color,
              }}
            >
              {card.value}
            </Typography>
            <Typography
              variant="cardSubtitle"
              sx={{
                mt: 0.5,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: isMobile ? 'normal' : 'nowrap',
              }}
            >
              {card.label}
            </Typography>
          </Box>
        </Paper>
      ))}
    </Box>
  );
};

export default DashboardSummaryCards;
